import type { ReactElement } from 'react';
import {
  useEffect,
  useMemo,
  useState,
  useCallback,
  isValidElement
} from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';

import { Restricted } from 'containers/Restricted';

import { useOrganisationId } from 'hooks/context';
import type { PermitAction } from 'hooks/usePermissions';

import { SidebarDropdownItem } from './SidebarDropdownItem';
import { SidebarSingleItem } from './SidebarSingleItem';

/**
 * All the following paths are prefixed with /org/
 * If you intend to add a route that is outside of the <RequireOrg> component, be aware that
 * you'll need to change the isActive and hasActiveChild function for it to work.
 */
type SidebarRoutePath =
  | '/'
  | '/crypto_funding'
  | '/payouts'
  | '/payout_drafts'
  | '/batches'
  | '/receivers'
  | '/collections'
  | '/collections/orders'
  | '/accounts'
  | '/accounts/withdrawals'
  | '/exports'
  | '/exported_payouts'
  | '/exported_payins'
  | '/exported_statements'
  | '/developers'
  | '/developers/authentication'
  | '/developers/webhooks'
  | '/developers/mcp'
  | '/configurations'
  | '/configurations/business_units'
  | '/users'
  | '/settings/profile';

export type SidebarMenuSubItem = Omit<SidebarMenuItem, 'icon'>;

export interface SidebarMenuItem {
  'data-testid': string;
  title: string;
  path: SidebarRoutePath;
  id?: string;
  end?: boolean;
  requiredPermission?: PermitAction;
  icon: React.ElementType;
  subtitle?: string;
  noActive?: boolean;
  subItems?: (SidebarMenuSubItem | ReactElement)[];
}

export interface SidebarItemProps {
  item: SidebarMenuItem;
  sidebarOpen?: boolean;
}

export const isSidebarMenuSubItem = (
  item: SidebarMenuSubItem | ReactElement
): item is SidebarMenuSubItem =>
  !isValidElement(item) &&
  typeof item === 'object' &&
  item !== null &&
  'path' in item;

const addOrgPrefix = ({
  path,
  organisationId
}: {
  path: SidebarRoutePath;
  organisationId: string;
}) =>
  path.startsWith(`/org/${organisationId}`)
    ? path
    : `/org/${organisationId}${path}`;

export const SidebarItem = ({
  item,
  sidebarOpen = true,
  ...props
}: SidebarItemProps) => {
  // what the dropdown looks like right now.
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // whether the user has explicitly closed an active dropdown, so we respect their preference and don’t pop it back open automatically.
  const [userCollapsedWhileActive, setUserCollapsedWhileActive] =
    useState(false);

  const organisationId = useOrganisationId();
  const navigate = useNavigate();
  const location = useLocation();

  const subItems = useMemo(
    () => (item.subItems ?? []).filter(isSidebarMenuSubItem),
    [item.subItems]
  );

  const matchesRoute = useCallback(
    (path: SidebarRoutePath, end?: boolean) =>
      Boolean(
        matchPath(
          {
            path: addOrgPrefix({ path, organisationId }),
            end: Boolean(end)
          },
          location.pathname
        )
      ),
    [location.pathname, organisationId]
  );

  const hasActiveChild = useMemo(
    () => subItems.some((subItem) => matchesRoute(subItem.path, subItem.end)),
    [matchesRoute, subItems]
  );

  const active = useMemo(
    () => matchesRoute(item.path, item.end) || hasActiveChild,
    [hasActiveChild, item.end, item.path, matchesRoute]
  );

  useEffect(() => {
    if (!sidebarOpen) {
      return;
    }

    if (active && !userCollapsedWhileActive) {
      setIsDropdownOpen(true);
    }

    if (!active) {
      setUserCollapsedWhileActive(false);
    }
  }, [sidebarOpen, active, userCollapsedWhileActive]);

  const shouldShowChildren = sidebarOpen && isDropdownOpen;

  const handleHeaderClick = useCallback(() => {
    if (!sidebarOpen) {
      setUserCollapsedWhileActive(false);
      // header account selection has a specific behavior where we select the first available account
      if (item.path === '/accounts') {
        const firstAccountPath = item?.subItems
          ?.filter((subItem) => isValidElement(subItem))
          .filter(
            (subItem) => subItem.key !== 'account-funding-dialog-item'
          )?.[0].key;

        navigate(
          addOrgPrefix({
            path: `/accounts/${firstAccountPath}` as SidebarRoutePath,
            organisationId
          })
        );
        return;
      }

      // otherwise, we select the first element in the subItems list
      if (subItems[0]) {
        navigate(addOrgPrefix({ path: subItems[0].path, organisationId }));
        return;
      }
    }

    const next = !isDropdownOpen;
    setIsDropdownOpen(next);

    if (active) {
      setUserCollapsedWhileActive(!next);
    }
  }, [
    active,
    isDropdownOpen,
    item.path,
    item?.subItems,
    navigate,
    organisationId,
    sidebarOpen,
    subItems
  ]);

  return (
    <Restricted requiredPermission={item.requiredPermission}>
      {item.subItems?.length ? (
        <SidebarDropdownItem
          isExpanded={shouldShowChildren}
          data-testid={item['data-testid']}
          item={item}
          onClick={handleHeaderClick}
          isActive={active}
          onSubItemClick={() => {
            if (!sidebarOpen) {
              return;
            }
            setIsDropdownOpen(true);
            setUserCollapsedWhileActive(false);
          }}
          sidebarOpen={sidebarOpen}
          {...props}
        />
      ) : (
        <SidebarSingleItem
          item={item}
          data-testid={item['data-testid']}
          {...props}
          isActive={active}
          sidebarOpen={sidebarOpen}
        />
      )}
    </Restricted>
  );
};
